import React from "react";
import styled from "styled-components";
import { TypographyNew } from "../../product/global/typography/typography";
import { useBreakpoint } from "../../product/utils/hooks";
import FooterLinks from "./FooterLinks";

const StyledFooter = styled.footer`
  display: flex;
  margin: 0 auto;
  padding-top: 80px;
  padding-bottom: 15px;
  width: 100vw;
  overflow: hidden;
  flex-direction: column;

  @media (min-width: 1400px) {
    width: 1400px;
  }
`;

const SocialIcon = styled.svg`
  height: 18px;
  color: ${(props) => props.theme.colors.primary1};
  margin: 10px;
`;

const BottomLinks = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-left: 20px;

  @media (min-width: 1400px) {
    flex-direction: row;
    margin-left: 0;
    align-items: center;
  }
`;

const TopLinks = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 60px;
  flex-direction: column;

  @media (min-width: 1400px) {
    flex-direction: row;
  }
`;

const LinksColumn = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 350px;
  flex: 1;
  margin-left: 20px;

  @media (min-width: 1400px) {
    margin-left: 0;
  }
`;

const BottomText = styled.p`
  font-size: 14px;
  font-weight: normal;
  color: ${(props) => props.theme.colors.grays2};
`;

const BottomLink = styled.a`
  font-size: 14px;
  text-decoration: none;
  margin: 5px 0;
  color: ${(props) => props.theme.colors.grays2};

  :hover {
    color: ${(props) => props.theme.colors.primary1};
  }
`;

const Heading = styled(TypographyNew)`
  font-weight: bold;
  margin: 10px 0;
  font-size: 14px;
`;

const TopLink = styled.a`
  font-size: 16px;
  text-decoration: none;
  color: ${(props) => props.theme.colors.primary1};
  margin: 10px 0;
  display: block;
`;

const Spacer = () => {
  const { isDesktop } = useBreakpoint();
  return isDesktop ? <span>&nbsp;·&nbsp;</span> : null;
};

const Footer = (): JSX.Element => {
  const { isMobile } = useBreakpoint();
  return (
    <StyledFooter>
      <TopLinks>
        <LinksColumn>
          {!isMobile && <Heading>HeavenHR Product</Heading>}
          <TopLink href="/rework">Recruiting</TopLink>
          <FooterLinks title="HR-Admin">
            <BottomLink href="/rework">Digital Personnel File</BottomLink>
            <BottomLink href="/rework">Onboarding &amp; Offboarding</BottomLink>
            <BottomLink href="/rework">Time Tracking</BottomLink>
            <BottomLink href="/rework">Absence management</BottomLink>
            <BottomLink href="/rework">Shift Planning</BottomLink>
            <BottomLink href="rework">See all features</BottomLink>
          </FooterLinks>
          <TopLink href="/rework">Payroll</TopLink>
        </LinksColumn>
        <LinksColumn>
          <FooterLinks title="Company">
            <BottomLink href="/rework">About HeavenHR</BottomLink>
            <BottomLink href="/rework">Careers — We’re hiring!</BottomLink>
            <BottomLink href="/rework">Pricing</BottomLink>
          </FooterLinks>
          <FooterLinks title="Resources">
            <BottomLink
              href="https://www.meetup.com/de-DE/Berlin-Vernetzung-mit-Personalfachleuten-Meetup-Gruppe/"
              target="_blank"
              rel="noreferrer"
            >
              Events
            </BottomLink>
            <BottomLink href="/rework">Blog</BottomLink>
            <BottomLink href="/rework">Glossary</BottomLink>
            <BottomLink href="/rework">FAQs</BottomLink>
          </FooterLinks>
        </LinksColumn>
        <LinksColumn>
          <div>
            <Heading>About HeavenHR</Heading>
            <BottomText>
              We have been developing a human resource management software since
              2015.
            </BottomText>
            <BottomText>
              Our cloud-based SaaS enables you to carry out necessary
              administration tasks quicker and more efficiently.
            </BottomText>
          </div>
          <div>
            <Heading>Contact</Heading>
            <TopLink href="tel:+49 30 555 743 990">+49 30 555 743 990</TopLink>
            <TopLink href="mailto:info@heavenhr.com">info@heavenhr.com</TopLink>
            <BottomText>Mon-Fri: 9 a.m. to 6 p.m.</BottomText>
            <div>
              <a
                href="https://twitter.com/heavenhr_de"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon viewBox="0 0 512 512">
                  <path
                    fill="currentColor"
                    d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558
                  298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299
                  49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772
                  6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3
                  27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214
                  12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36
                  14.294-52.954 51.655 63.675 129.3 105.258 216.365
                  109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934
                  104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32
                  66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122
                  60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"
                  ></path>
                </SocialIcon>
              </a>
              <a
                href="https://www.linkedin.com/company/heavenhr"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon viewBox="0 0 448 512">
                  <path
                    fill="currentColor"
                    d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1
                107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9
                448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69
                76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28
                61.9 111.28 142.3V448z"
                  ></path>
                </SocialIcon>
              </a>
              <a
                href="https://www.xing.com/company/heavenhr"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon viewBox="0 0 384 512">
                  <path
                    fill="currentColor"
                    d="M162.7 210c-1.8 3.3-25.2 44.4-70.1 123.5-4.9 8.3-10.8 12.5-17.7 12.5H9.8c-7.7
                0-12.1-7.5-8.5-14.4l69-121.3c.2 0 .2-.1 0-.3l-43.9-75.6c-4.3-7.8.3-14.1
                8.5-14.1H100c7.3 0 13.3 4.1 18 12.2l44.7 77.5zM382.6 46.1l-144 253v.3L330.2 466c3.9
                7.1.2 14.1-8.5 14.1h-65.2c-7.6 0-13.6-4-18-12.2l-92.4-168.5c3.3-5.8 51.5-90.8
                144.8-255.2 4.6-8.1 10.4-12.2 17.5-12.2h65.7c8 0 12.3 6.7 8.5 14.1z"
                  ></path>
                </SocialIcon>
              </a>
              <a
                href="https://www.meetup.com/de-DE/Berlin-Vernetzung-mit-Personalfachleuten-Meetup-Gruppe/"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon viewBox="0 0 512 512">
                  <path
                    fill="currentColor"
                    d="M99 414.3c1.1 5.7-2.3 11.1-8 12.3-5.4 1.1-10.9-2.3-12-8-1.1-5.4 2.3-11.1 7.7-12.3
                5.4-1.2 11.1 2.3 12.3 8zm143.1 71.4c-6.3 4.6-8 13.4-3.7 20 4.6 6.6 13.4 8.3 20 3.7
                6.3-4.6 8-13.4 3.4-20-4.2-6.5-13.1-8.3-19.7-3.7zm-86-462.3c6.3-1.4 10.3-7.7
                8.9-14-1.1-6.6-7.4-10.6-13.7-9.1-6.3 1.4-10.3 7.7-9.1 14 1.4 6.6 7.6 10.6 13.9
                9.1zM34.4 226.3c-10-6.9-23.7-4.3-30.6 6-6.9 10-4.3 24 5.7 30.9 10 7.1 23.7 4.6
                30.6-5.7 6.9-10.4 4.3-24.1-5.7-31.2zm272-170.9c10.6-6.3 13.7-20
                7.7-30.3-6.3-10.6-19.7-14-30-7.7s-13.7 20-7.4 30.6c6 10.3 19.4 13.7 29.7 7.4zm-191.1
                58c7.7-5.4 9.4-16 4.3-23.7s-15.7-9.4-23.1-4.3c-7.7 5.4-9.4 16-4.3 23.7 5.1 7.8 15.6
                9.5 23.1 4.3zm372.3 156c-7.4 1.7-12.3 9.1-10.6 16.9 1.4 7.4 8.9 12.3 16.3 10.6
                7.4-1.4 12.3-8.9
                10.6-16.6-1.5-7.4-8.9-12.3-16.3-10.9zm39.7-56.8c-1.1-5.7-6.6-9.1-12-8-5.7
                1.1-9.1 6.9-8 12.6 1.1 5.4 6.6 9.1 12.3 8 5.4-1.5 9.1-6.9 7.7-12.6zM447 138.9c-8.6
                6-10.6 17.7-4.9 26.3 5.7 8.6 17.4 10.6 26 4.9 8.3-6 10.3-17.7
                4.6-26.3-5.7-8.7-17.4-10.9-25.7-4.9zm-6.3 139.4c26.3 43.1 15.1 100-26.3 129.1-17.4
                12.3-37.1 17.7-56.9 17.1-12 47.1-69.4 64.6-105.1 32.6-1.1.9-2.6 1.7-3.7 2.9-39.1
                27.1-92.3
                17.4-119.4-22.3-9.7-14.3-14.6-30.6-15.1-46.9-65.4-10.9-90-94-41.1-139.7-28.3-46.9.6-107.4
                53.4-114.9C151.6 70 234.1 38.6 290.1 82c67.4-22.3 136.3 29.4 130.9 101.1 41.1 12.6
                52.8 66.9 19.7 95.2zm-70 74.3c-3.1-20.6-40.9-4.6-43.1-27.1-3.1-32 43.7-101.1
                40-128-3.4-24-19.4-29.1-33.4-29.4-13.4-.3-16.9 2-21.4 4.6-2.9 1.7-6.6
                4.9-11.7-.3-6.3-6-11.1-11.7-19.4-12.9-12.3-2-17.7 2-26.6 9.7-3.4 2.9-12 12.9-20
                9.1-3.4-1.7-15.4-7.7-24-11.4-16.3-7.1-40 4.6-48.6 20-12.9 22.9-38 113.1-41.7
                125.1-8.6 26.6 10.9 48.6 36.9 47.1 11.1-.6 18.3-4.6 25.4-17.4 4-7.4 41.7-107.7
                44.6-112.6 2-3.4 8.9-8 14.6-5.1 5.7 3.1 6.9 9.4 6 15.1-1.1 9.7-28 70.9-28.9 77.7-3.4
                22.9 26.9 26.6 38.6 4 3.7-7.1 45.7-92.6 49.4-98.3 4.3-6.3 7.4-8.3 11.7-8 3.1 0 8.3.9
                7.1 10.9-1.4 9.4-35.1 72.3-38.9 87.7-4.6 20.6 6.6 41.4 24.9 50.6 11.4 5.7 62.5 15.7
                58.5-11.1zm5.7 92.3c-10.3 7.4-12.9 22-5.7 32.6 7.1 10.6 21.4 13.1 32 6 10.6-7.4
                13.1-22 6-32.6-7.4-10.6-21.7-13.5-32.3-6z"
                  ></path>
                </SocialIcon>
              </a>
              <a
                href="https://www.instagram.com/heavenhr_de/"
                target="_blank"
                rel="noreferrer"
              >
                <SocialIcon viewBox="0 0 448 512">
                  <path
                    fill="currentColor"
                    d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9
                287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7
                33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9
                0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1
                27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9
                0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9
                9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9
                93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8
                19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7
                2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7
                9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6
                7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"
                  ></path>
                </SocialIcon>
              </a>
            </div>
          </div>
        </LinksColumn>
      </TopLinks>
      <BottomLinks>
        <BottomLink>{`© ${new Date().getFullYear()} HeavenHR GmbH`}</BottomLink>
        <Spacer />
        <BottomLink href="rework">Data protection &amp; Safety</BottomLink>
        <Spacer />
        <BottomLink href="rework">
          Terms &amp; Conditions for Companies
        </BottomLink>
        <Spacer />
        <BottomLink href="rework">
          Terms &amp; Conditions for Employees
        </BottomLink>
        <Spacer />
        <BottomLink href="rework">Imprint</BottomLink>
      </BottomLinks>
    </StyledFooter>
  );
};

export default Footer;
